import React, { useState } from 'react'
import { Layers, AlertCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CompareSelector } from '@/components/v11/compare'
import { DataLoader } from '@/components/v11/discovery'
import {
  ComparisonDimensionPickerModal,
  ComparisonValuePickerModal,
} from '@/components/v11/compare/modals'
import type { ValueOption } from '@/components/v11/compare/modals'
import {
  useCompareSubjectsQuery,
  canCompareSubjects,
  getComparisonResult,
} from '@/hooks/useCompareSubjects'
import {
  useDebouncedSubjectSearch,
  getSearchResults,
} from '@/hooks/useSubjectSearch'
import { useSubjectLists, getListItems } from '@/hooks/useSubjectLists'
import { OverlapVenn } from '@/components/v11/overlap/OverlapVenn'
import { OverlapAudienceProfile } from '@/components/v11/overlap/OverlapAudienceProfile'
import { OverlapInsightSummary } from '@/components/v11/overlap/OverlapInsightSummary'
import { OverlapStatCards } from '@/components/v11/overlap/OverlapStatCards'
import type {
  SubjectSelection,
  DimensionTypeConfig,
  SearchableDimensionType,
  ListDimensionType,
  SearchResultItem,
  ListResultItem,
} from '@/types/comparison'

const EMPTY_SELECTION: SubjectSelection = {
  type: null,
  item: null,
}

export const FeltonOverlap: React.FC = () => {
  // Subject selections
  const [subjectA, setSubjectA] = useState<SubjectSelection>(EMPTY_SELECTION)
  const [subjectB, setSubjectB] = useState<SubjectSelection>(EMPTY_SELECTION)

  // Modal states
  const [activeSubject, setActiveSubject] = useState<'A' | 'B' | null>(null)
  const [dimensionModalOpen, setDimensionModalOpen] = useState(false)
  const [valueModalOpen, setValueModalOpen] = useState(false)
  const [selectedDimension, setSelectedDimension] =
    useState<DimensionTypeConfig | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const isSearchable = selectedDimension?.searchable ?? false
  const isChannelDimension = selectedDimension?.type === 'channel'
  const isPostDimension = selectedDimension?.type === 'post'
  const isPreloadedDimension = isChannelDimension || isPostDimension

  // Search results (for searchable dimensions)
  const searchQuery_ = useDebouncedSubjectSearch(
    (selectedDimension?.type as SearchableDimensionType) || 'brand',
    searchQuery,
    300,
    {
      enabled: isSearchable && valueModalOpen && (isPreloadedDimension || searchQuery.length >= 2),
      minQueryLength: isPreloadedDimension ? 0 : 2,
    }
  )

  // List items (for fixed list dimensions)
  const listQuery = useSubjectLists(
    (selectedDimension?.type as ListDimensionType) || 'platform',
    { enabled: !isSearchable && valueModalOpen }
  )

  const values: ValueOption[] = isSearchable
    ? getSearchResults(searchQuery_.data).map((item) => ({
        id: item.id,
        name: item.name,
        subtitle: item.subtitle,
        platform: item.platform,
        count: item.count,
      }))
    : getListItems(listQuery.data).map((item) => ({
        id: item.id,
        name: item.name,
        subtitle: item.subtitle,
      }))

  // Build comparison subjects
  const comparisonSubjectA =
    subjectA.type && subjectA.item
      ? { type: subjectA.type, id: subjectA.item.id }
      : null
  const comparisonSubjectB =
    subjectB.type && subjectB.item
      ? { type: subjectB.type, id: subjectB.item.id }
      : null

  const canCompare = canCompareSubjects(comparisonSubjectA, comparisonSubjectB)

  // Fetch comparison data (auto-fetches when both subjects are selected)
  const {
    data: comparisonData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useCompareSubjectsQuery(comparisonSubjectA, comparisonSubjectB, {
    enabled: canCompare,
  })

  const comparisonResult = getComparisonResult(comparisonData)

  // Modal handlers
  const handleOpenDimensionPicker = (subject: 'A' | 'B') => {
    setActiveSubject(subject)
    setDimensionModalOpen(true)
  }

  const handleDimensionSelect = (dimension: DimensionTypeConfig) => {
    setSelectedDimension(dimension)
    setDimensionModalOpen(false)
    setSearchQuery('')
    setValueModalOpen(true)
  }

  const handleValueSelect = (value: SearchResultItem | ListResultItem) => {
    const selection: SubjectSelection = {
      type: selectedDimension?.type ?? null,
      item: value,
    }

    if (activeSubject === 'A') {
      setSubjectA(selection)
    } else if (activeSubject === 'B') {
      setSubjectB(selection)
    }

    setValueModalOpen(false)
    setSelectedDimension(null)
    setSearchQuery('')
    setActiveSubject(null)
  }

  const handleCloseDimensionModal = () => {
    setDimensionModalOpen(false)
    setSelectedDimension(null)
    setActiveSubject(null)
  }

  const handleCloseValueModal = () => {
    setValueModalOpen(false)
    setSearchQuery('')
  }

  const handleCompare = () => {
    if (canCompare) refetch()
  }

  const handleClearAll = () => {
    setSubjectA(EMPTY_SELECTION)
    setSubjectB(EMPTY_SELECTION)
  }

  const hasAnySelection =
    (subjectA.type && subjectA.item) || (subjectB.type && subjectB.item)

  // Extract overlap data from result
  const overlap = comparisonResult?.overlap
  const totalA =
    comparisonResult?.subjectA.aggregations.vanity.totalProfiles ?? 0
  const totalB =
    comparisonResult?.subjectB.aggregations.vanity.totalProfiles ?? 0

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
          <Layers className="h-6 w-6 text-orange-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-v11-text-primary">
            Audience Overlap
          </h1>
          <p className="text-sm text-v11-text-muted">
            Discover where your audiences converge
          </p>
        </div>
      </div>

      {/* Subject Selectors */}
      <div className="flex items-center gap-4 flex-wrap">
        <CompareSelector
          variant="A"
          subject={subjectA}
          onClick={() => handleOpenDimensionPicker('A')}
        />

        <div className="px-4 py-2 bg-orange-500/15 text-orange-400 text-sm font-bold rounded-full border border-orange-500/30">
          ∩
        </div>

        <CompareSelector
          variant="B"
          subject={subjectB}
          onClick={() => handleOpenDimensionPicker('B')}
        />

        {hasAnySelection && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="ml-auto text-muted-foreground hover:text-destructive"
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Results Section */}
      <div className="pt-4">
        {/* Error State */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : 'An unexpected error occurred.'}
              <Button
                variant="outline"
                size="sm"
                onClick={handleCompare}
                className="ml-4"
              >
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && canCompare && (
          <div className="flex justify-center py-6">
            <DataLoader dataSource="overlap" variant="initial" />
          </div>
        )}

        {/* Results View */}
        {!isLoading && comparisonResult && overlap && (
          <div className="space-y-6">
            {/* Venn Diagram */}
            <OverlapVenn
              subjectA={{ name: comparisonResult.subjectA.name, total: totalA }}
              subjectB={{ name: comparisonResult.subjectB.name, total: totalB }}
              overlapCount={overlap.count}
              percentA={overlap.percentA}
              percentB={overlap.percentB}
            />

            {/* Overlap Stats (Only A | Shared | Only B) */}
            <OverlapStatCards
              subjectAName={comparisonResult.subjectA.name}
              subjectBName={comparisonResult.subjectB.name}
              totalA={totalA}
              totalB={totalB}
              overlapCount={overlap.count}
              percentA={overlap.percentA}
              percentB={overlap.percentB}
            />

            {/* Insight Summary - hidden for now */}
            {/* {overlap.aggregations && (
              <OverlapInsightSummary
                aggregations={overlap.aggregations}
                overlapCount={overlap.count}
              />
            )} */}

            {/* Shared Audience Profile */}
            {overlap.aggregations && (
              <OverlapAudienceProfile
                aggregations={overlap.aggregations}
                overlapCount={overlap.count}
              />
            )}

            {/* No Overlap State */}
            {overlap.count === 0 && (
              <div className="bg-v11-surface border border-v11-border rounded-lg p-8 text-center">
                <Layers className="h-12 w-12 text-v11-text-muted mx-auto mb-3 opacity-30" />
                <h3 className="text-lg font-semibold text-v11-text-primary mb-1">
                  No Overlap Found
                </h3>
                <p className="text-sm text-v11-text-muted max-w-md mx-auto">
                  These two audiences have no shared profiles. Try selecting
                  broader subjects.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && !comparisonResult && (
          <div className="bg-v11-surface border border-v11-border rounded-lg p-12 text-center">
            <Layers className="h-16 w-16 text-v11-text-muted mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-semibold text-v11-text-primary mb-2">
              Ready to Analyze Overlap
            </h3>
            <p className="text-sm text-v11-text-muted max-w-md mx-auto">
              Select two subjects above to discover their shared audience
              profile — who they have in common and what that audience looks
              like.
            </p>
            <div className="mt-6 space-y-2 text-xs text-v11-text-muted">
              <p className="font-medium">Examples:</p>
              <ul className="space-y-1">
                <li>Nike ∩ Adidas — shared brand followers</li>
                <li>Instagram ∩ TikTok — cross-platform users</li>
                <li>Lakers ∩ Gen Z — team fans in a generation</li>
                <li>Persona A ∩ Persona B — saved audience overlap</li>
              </ul>
            </div>
          </div>
        )}

        {/* Refreshing Indicator */}
        {isFetching && !isLoading && comparisonResult && (
          <div className="flex items-center justify-center gap-2 text-sm text-v11-text-muted py-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Refreshing data...</span>
          </div>
        )}
      </div>

      {/* Modals */}
      <ComparisonDimensionPickerModal
        open={dimensionModalOpen}
        onClose={handleCloseDimensionModal}
        onSelectDimension={handleDimensionSelect}
      />

      {selectedDimension && (
        <ComparisonValuePickerModal
          open={valueModalOpen}
          onClose={handleCloseValueModal}
          dimensionLabel={selectedDimension.label}
          values={values}
          isLoading={
            isSearchable ? searchQuery_.isLoading : listQuery.isLoading
          }
          searchMode={isSearchable}
          onSearch={setSearchQuery}
          isSearching={searchQuery_.isFetching}
          onApply={handleValueSelect}
          preloaded={isPreloadedDimension}
        />
      )}
    </div>
  )
}
